import { Platform } from 'react-native';
import PantryTypes from '../../constants/PantryTypes';
import { isPlentifulProvider } from '../../Location';
import { logError } from '../../utils/logger';

// cross-platform asset path resolution doesn't like the ../../ here
const ICON_PATH = 'assets/images/marker_icons';

const MARKER_ICON =
  Platform.OS === 'web'
    ? {
        plentiful_pantry_qr: require(`../../${ICON_PATH}/category/plentiful_pantry_qr.svg`),
        plentiful_pantry_non_qr: require(`../../${ICON_PATH}/category/plentiful_pantry_non_qr.svg`),
        other_pantry_verified: require(`../../${ICON_PATH}/category/other_pantry_verified.svg`),
        other_pantry_unverified: require(`../../${ICON_PATH}/category/other_pantry_unverified.svg`),
        hot_meal_verified: require(`../../${ICON_PATH}/category/hot_meal_verified.svg`),
        hot_meal_unverified: require(`../../${ICON_PATH}/category/hot_meal_unverified.svg`),
        non_food_verified: require(`../../${ICON_PATH}/category/non_food_verified.svg`),
        non_food_unverified: require(`../../${ICON_PATH}/category/non_food_unverified.svg`),
      }
    : {
        plentiful_pantry_qr: require(`../../${ICON_PATH}/category/plentiful_pantry_qr.png`),
        plentiful_pantry_non_qr: require(`../../${ICON_PATH}/category/plentiful_pantry_non_qr.png`),
        other_pantry_verified: require(`../../${ICON_PATH}/category/other_pantry_verified.png`),
        other_pantry_unverified: require(`../../${ICON_PATH}/category/other_pantry_unverified.png`),
        hot_meal_verified: require(`../../${ICON_PATH}/category/hot_meal_verified.png`),
        hot_meal_unverified: require(`../../${ICON_PATH}/category/hot_meal_unverified.png`),
        non_food_verified: require(`../../${ICON_PATH}/category/non_food_verified.png`),
        non_food_unverified: require(`../../${ICON_PATH}/category/non_food_unverified.png`),
      };

/**
 * Map marker icons for PantryMap and PantryDetails components.
 * Native: @2x/@3x sibling files required for correct sizing — see
 * https://github.com/react-native-maps/react-native-maps/issues/1422#issuecomment-323540433
 * Exported so other marker implementations (e.g. components/map/siteMarkerIcon.ts) can reuse the
 * same pin assets without re-requiring the files or depending on this file's Pantry-shaped lookup
 * functions.
 */
export const MAP_MARKER_ICONS =
  Platform.OS === 'web'
    ? {
        inActive: require('../../assets/images/web-inactive_pin_small.png'),
        active: require('../../assets/images/web-active_pin_small.png'),
        otherPantry: require('../../assets/images/web-otherpantry_pin_small.png'),
      }
    : {
        inActive: require('../../assets/images/mobile-native-inactive_pin_small.png'),
        active: require('../../assets/images/mobile-native-active_pin_small.png'),
        otherPantry: require('../../assets/images/mobile-native-otherpantry_pin_small.png'),
      };

/**
 * Get the appropriate map marker icon for PantryMap and PantryDetails components.
 * This uses the simple icon system that works reliably across platforms.
 */
export const getMapMarkerIcon = (pantry, selectedPantryId = null) => {
  if (pantry.id === selectedPantryId) {
    return MAP_MARKER_ICONS.active;
  }

  if (!isPlentifulProvider(pantry)) {
    return MAP_MARKER_ICONS.otherPantry;
  }
  return MAP_MARKER_ICONS.inActive;
};

/**
 * Using the data returned from the API, choose the most appropriate marker icon. This categorization
 * logic must match the logic in the backend, currently in `searchAlgoliaPantryIndex`.
 */
export const getMarkerIcon = (pantry) => {
  if (pantry.type === PantryTypes.plentiful_provider) {
    return pantry.use_qr_codes ? MARKER_ICON.plentiful_pantry_qr : MARKER_ICON.plentiful_pantry_non_qr;
  }

  if (pantry.pantry_type === PantryTypes.plentiful_provider) {
    return pantry.use_qr_codes ? MARKER_ICON.plentiful_pantry_qr : MARKER_ICON.plentiful_pantry_non_qr;
  }

  if (pantry.pantry_type === PantryTypes.other_pantry) {
    return pantry.verified ? MARKER_ICON.other_pantry_verified : MARKER_ICON.other_pantry_unverified;
  }

  if (pantry.pantry_type === PantryTypes.hot_meal) {
    return pantry.verified ? MARKER_ICON.hot_meal_verified : MARKER_ICON.hot_meal_unverified;
  }

  if (pantry.pantry_type === PantryTypes.non_food) {
    return pantry.verified ? MARKER_ICON.non_food_verified : MARKER_ICON.non_food_unverified;
  }

  logError(new Error('Unknown pantry type'), { pantryType: pantry.pantry_type, pantryId: pantry.id });
  return MAP_MARKER_ICONS.otherPantry;
};
